import { colors, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactNode } from 'react'

/**
 * A poll on the in-person stage as the room reads it
 * (`useStageReadByTheRoom`): the stage's whole width, and, when there is
 * one, a column at the right as narrow as the knowledge check's for how the
 * room is answering.
 *
 * The height is the stage's less the 20px the word cloud leaves under its
 * card, so the foot of both is on the same line.
 */
const Card = styled.section<{ $side: boolean }>`
	align-self: stretch;
	background-color: ${colors.white};
	border-radius: 18px;
	box-shadow: 0 3px 6px 0 ${colors.black15};
	display: grid;
	grid-template-columns: ${props =>
		props.$side ? 'minmax(0, 1fr) clamp(300px, 28%, 420px)' : 'minmax(0, 1fr)'};
	margin-bottom: 20px;
	min-height: 0;
	overflow: hidden;
	width: 100%;
`

const Content = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 0;
	min-width: 0;
	overflow-y: auto;
	padding: 20px 40px;

	${ui.responsive.desktopFHD} {
		padding: 24px 56px;
	}
`

const Side = styled.aside`
	border-left: 1px solid ${colors.borderLightGray};
	min-height: 0;
	min-width: 0;
	overflow-y: auto;
	padding: 20px 32px;

	${ui.responsive.desktopFHD} {
		padding: 24px 40px;
	}
`

type Props = {
	/** The column at the right, and what it is called for assistive tech. */
	side?: { label: string; content: ReactNode }
	testId?: string
}

export const PollStageCard: FC<Props> = ({ side, testId, children }) => (
	<Card $side={!!side} data-testid={testId}>
		<Content>{children}</Content>
		{side ? <Side aria-label={side.label}>{side.content}</Side> : null}
	</Card>
)

/** What kind of poll this is, above its question. */
export const PollEyebrow = styled.div`
	color: ${colors.warmGrey};
	font-size: 18px;
	font-weight: 500;
	line-height: 1.3;

	${ui.responsive.desktop1440} {
		font-size: 20px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 26px;
	}
`

/** The poll's question, the largest type on the stage. */
export const PollQuestion = styled.h2`
	color: ${colors.blackish};
	font-size: 30px;
	font-weight: 700;
	line-height: 1.2;
	margin: 6px 0 0;
	overflow-wrap: anywhere;

	${ui.responsive.desktop1440} {
		font-size: 36px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 48px;
	}
`

/** A line under the question the room should know, such as "choose more than one". */
export const PollNote = styled.p`
	color: ${colors.warmGrey};
	font-size: 18px;
	line-height: 1.4;
	margin: 8px 0 0;

	${ui.responsive.desktop1440} {
		font-size: 20px;
	}

	${ui.responsive.desktopFHD} {
		font-size: 26px;
	}
`
